/**
 * Student Context
 * Manages student list state and operations for the spinner application
 */

import { useBridge } from '@/hooks/useBridge'
import type {
  Student,
  StudentPickedEvent,
  StudentRemovedEvent,
} from '@/services/bridge/types/bridge.types'
import { matchStudent, normalizeStudentList } from '@/utils'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { MOCK_STUDENTS } from '@/config/mockData'

interface StudentContextType {
  // State
  students: Student[]
  isLoading: boolean
  error: string | null

  // Actions
  loadStudents: () => Promise<void>
  pickStudent: (event: StudentPickedEvent) => Promise<boolean>
  removeStudent: (event: StudentRemovedEvent) => Promise<boolean>
}

const StudentContext = createContext<StudentContextType | undefined>(undefined)

interface StudentProviderProps {
  children: React.ReactNode
  autoLoad?: boolean
}

export const StudentProvider: React.FC<StudentProviderProps> = ({
  children,
  autoLoad = true,
}) => {
  const {
    getStudentList,
    studentPicked,
    studentRemoved,
    error: bridgeError,
    isConnected: bridgeConnected,
  } = useBridge()
  const [students, setStudents] = useState<Student[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Native host 為 student list 的真相來源。重新 load 等同 refresh —
  // 先前被 removeStudent 移除（但 host 仍保留）的學生會在重 load 後再次出現。
  const loadStudents = useCallback(async () => {
    setIsLoading(true)
    setError(null)

    try {
      const studentList = await getStudentList()
      const normalizedStudents = normalizeStudentList(studentList)
      setStudents(normalizedStudents)

      console.log(
        `[StudentContext] 載入 ${normalizedStudents.length} 個學生資料`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to load students'
      setError(errorMessage)
      console.error('Failed to load students:', err)

      // Use mock data in development if Bridge fails
      if (import.meta.env.DEV) {
        console.log('Using mock student data for development')
        const normalizedMOCK_STUDENTS = normalizeStudentList(MOCK_STUDENTS)
        setStudents(normalizedMOCK_STUDENTS)
        setError(null)
      }
    } finally {
      setIsLoading(false)
    }
  }, [getStudentList])

  /**
   * Send pick event to bridge AND update local list view if needed.
   * 目前 pick 不影響 list state（轉盤 session-local 的選中狀態由呼叫端管理），
   * 僅用 matchStudent 驗證 event 對應到 list 中存在的學生，否則記錄 warning。
   * Bridge 失敗回傳 false（錯誤已由 useBridge 寫入 error state），不會 throw。
   */
  const pickStudent = useCallback(
    async (event: StudentPickedEvent): Promise<boolean> => {
      const target = students.find(s => matchStudent(event, s))
      if (!target) {
        console.warn('[StudentContext] pickStudent: no matching student', event)
        return false
      }

      try {
        return await studentPicked(event)
      } catch {
        return false
      }
    },
    [students, studentPicked]
  )

  /**
   * Send remove event to bridge AND remove from local students state.
   * UI 一致性優先：本地 state 一律先移除，bridge 失敗回傳 false 但不 restore，
   * 使用者按 refresh 可從 host 拉回正確名單。不會 throw。
   */
  const removeStudent = useCallback(
    async (event: StudentRemovedEvent): Promise<boolean> => {
      const target = students.find(s => matchStudent(event, s))
      if (!target) {
        console.warn(
          '[StudentContext] removeStudent: no matching student',
          event
        )
        return false
      }

      setStudents(prev => prev.filter(s => !matchStudent(event, s)))
      try {
        return await studentRemoved(event)
      } catch {
        return false
      }
    },
    [students, studentRemoved]
  )

  // Sync error state from bridge
  useEffect(() => {
    if (bridgeError) {
      setError(bridgeError)
    }
  }, [bridgeError])

  // Auto-load students on mount if enabled (等待 Bridge 連接後)
  useEffect(() => {
    if (autoLoad && bridgeConnected) {
      loadStudents()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoLoad, bridgeConnected])

  const contextValue = useMemo<StudentContextType>(
    () => ({
      students,
      isLoading,
      error,

      loadStudents,
      pickStudent,
      removeStudent,
    }),
    [students, isLoading, error, loadStudents, pickStudent, removeStudent]
  )

  return (
    <StudentContext.Provider value={contextValue}>
      {children}
    </StudentContext.Provider>
  )
}

/**
 * Hook to use Student Context
 */
export const useStudents = (): StudentContextType => {
  const context = useContext(StudentContext)
  if (!context) {
    throw new Error('useStudents must be used within a StudentProvider')
  }
  return context
}

export default StudentContext
